/**
 * How the review fixes an unrecognized row (`UNRECOGNIZED_INSTRUMENT`). Fixed with a new alias, it is
 * resolved under that alias and sized on Cold War by its file quantity, else its file weight, as an
 * uploaded row is; its cells stay read-only, the values are Cold War's. An alias the engine does not
 * recognise either, or a classification in place of an alias, makes it a synthetic instrument
 * (`./synthetic`). Every new fix puts the row back on its file values first and drops any answer still
 * out for the previous one (`attempt`).
 *
 * `beginUnrecognizedFix` and `applyAliasOutcome` are pure; `sizeNewAlias` is the only call to the API.
 */
import type { Currencies } from "@eu.mdotm.api/sphere";
import { unreachable } from "@mdotm/utils/runtime";
import { isHeldByValueByNature } from "$root/functional-areas/instruments-editor/quantity/heldByValue";
import { resolveInstruments } from "$root/functional-areas/instruments-editor/quantity/new-instruments/resolve";
import {
	parseSizingAnswer,
	type SizingAnswer,
} from "$root/functional-areas/instruments-editor/quantity/position-sizing/answer";
import { positionSizingRepository } from "$root/functional-areas/instruments-editor/quantity/position-sizing/repository";
import {
	applySizingAnswer,
	failSizing,
	isCurrentSizing,
	type SizingEdit,
	sizingRequest,
	startSizing,
} from "$root/functional-areas/instruments-editor/quantity/position-sizing/sizing";
import { fileSizing } from "$root/functional-areas/instruments-editor/quantity/upload/uploadedQuantity";
import { type QuantityReviewRow, rebalanceReviewCash } from "./rows";
import { toSynthetic } from "./synthetic";

export type AliasSizingContext = { aum: number; currency: Currencies | undefined; date: string };

export type AliasOutcome =
	| { kind: "answer"; tickerName: string; heldByValue: boolean; answer: SizingAnswer }
	| { kind: "synthetic" }
	| { kind: "quantityOnHeldByValue" }
	| { kind: "failed"; message: string };

/**
 * The row put back on its file values, then — when it carries a new alias — waiting on Cold War for
 * the file's value, or flagged `aliasUnsized` when the file gives it none. Without a new alias it was
 * classified, and turns synthetic, keeping the market value typed on it if it already was.
 */
export function beginUnrecognizedFix(
	row: QuantityReviewRow,
	file: QuantityReviewRow,
	params: { attempt: number; now: string; aum: number },
): QuantityReviewRow {
	const reset: QuantityReviewRow = {
		...row,
		tickerName: file.tickerName,
		quantity: file.quantity,
		price: file.price,
		marketValue: file.marketValue,
		weight: file.weight,
		heldByValue: file.heldByValue,
		unpriced: file.unpriced,
		positionSizing: undefined,
		aliasUnsized: undefined,
		synthetic: undefined,
	};
	if (!row.newAlias) {
		return toSynthetic(row.synthetic ? { ...reset, marketValue: row.marketValue } : reset, params.aum);
	}
	const edit = fileSizing(file);
	if (!edit) {
		return { ...reset, aliasUnsized: true };
	}
	const { attempt, now } = params;
	return { ...reset, positionSizing: startSizing(reset, { edit, attempt, now }).positionSizing };
}

/**
 * Resolves the row's new alias and sizes the instrument it names by `edit`. An alias that names no
 * instrument the engine recognises answers `synthetic`, with nothing sized. An instrument held by value
 * has no share count, so a file quantity cannot size it: the alias is refused, as the upload's
 * verification refuses a quantity on such an instrument.
 */
export async function sizeNewAlias(params: {
	investmentUuid: string;
	newAlias: string;
	edit: SizingEdit;
	file: QuantityReviewRow;
	context: AliasSizingContext;
}): Promise<AliasOutcome> {
	const { investmentUuid, newAlias, file, context } = params;
	try {
		const [resolved] = await resolveInstruments({
			investmentUuid,
			instruments: [{ identifier: newAlias }],
			referenceDate: context.date,
		});
		if (!resolved?.ticker || resolved.issue === "UNRECOGNIZED_INSTRUMENT") {
			return { kind: "synthetic" };
		}
		const row: QuantityReviewRow = { ...file, tickerName: resolved.ticker };
		const heldByValue = resolved.holdingMode === "WEIGHT" || isHeldByValueByNature(row);
		if (heldByValue && params.edit.field === "QUANTITY") {
			return { kind: "quantityOnHeldByValue" };
		}
		const answer = parseSizingAnswer(
			await positionSizingRepository.deriveInstrumentValue(
				investmentUuid,
				sizingRequest({ ...row, heldByValue }, params.edit, context),
			),
		);
		return { kind: "answer", tickerName: resolved.ticker, heldByValue, answer };
	} catch (error) {
		return { kind: "failed", message: error instanceof Error ? error.message : String(error) };
	}
}

/**
 * Writes the outcome of sizing `attempt` into row `id` and re-derives cash. An outcome for a sizing the
 * row has moved on from changes nothing.
 */
export function applyAliasOutcome(params: {
	rows: QuantityReviewRow[];
	id: string;
	attempt: number;
	outcome: AliasOutcome;
	context: AliasSizingContext;
	now: string;
}): QuantityReviewRow[] {
	const { rows, id, attempt, outcome, context, now } = params;
	const target = rows.find((row) => row.id === id);
	if (!isCurrentSizing(target, attempt)) {
		return rows;
	}
	return rebalanceReviewCash(
		rows.map((row) => (row.id === id ? settle(row, attempt, outcome, context, now) : row)),
		context.aum,
	);
}

function settle(
	row: QuantityReviewRow,
	attempt: number,
	outcome: AliasOutcome,
	context: AliasSizingContext,
	now: string,
): QuantityReviewRow {
	switch (outcome.kind) {
		case "failed":
			return { ...row, positionSizing: failSizing(row, { attempt, now, message: outcome.message })?.positionSizing };
		case "synthetic":
			return toSynthetic(row, context.aum);
		case "quantityOnHeldByValue":
			return { ...row, newAlias: undefined, hasBeenReviewed: false, positionSizing: undefined };
		case "answer": {
			const resolved: QuantityReviewRow = { ...row, tickerName: outcome.tickerName, heldByValue: outcome.heldByValue };
			try {
				const sized = applySizingAnswer({
					rows: [resolved],
					id: row.id,
					attempt,
					answer: outcome.answer,
					aum: context.aum,
					date: context.date,
				})?.rows[0];
				if (!sized) {
					return row;
				}
				return {
					...resolved,
					quantity: sized.quantity,
					price: sized.price,
					marketValue: sized.marketValue,
					weight: sized.weight,
					heldByValue: sized.heldByValue,
					unpriced: sized.unpriced,
					pricingByDate: sized.pricingByDate,
					positionSizing: sized.positionSizing,
				};
			} catch (error) {
				const message = error instanceof Error ? error.message : String(error);
				return { ...resolved, positionSizing: failSizing(resolved, { attempt, now, message })?.positionSizing };
			}
		}
		default:
			return unreachable(outcome);
	}
}
